import { useState } from 'react';

import useRetrieve from 'src/compass/shared/hooks/reactQuery/useRetrieve';
import { useCasAuthenticated } from 'src/compass/shared/hooks/useCasAuthenticated';
import { AppPermissions, useUserPermissions } from 'src/compass/site/routes/useUserPermissions';

export const PENDING_EMAILS_QUERY_KEY = 'Msg/History/Pending';

/*
  Everyone who can send a message the indicator tracks: SCE Reports sends, customer messages from the
  Customers pages and Claim Tracking's due reminders. Msg/History/Pending accepts the same three, so
  a sender holding only CUSTOMERS or CLAIM_TRACKING still hears that a message of theirs failed.
*/
const PENDING_MESSAGE_SENDERS = [
  AppPermissions.SCE_REPORTS,
  AppPermissions.CUSTOMERS,
  AppPermissions.CLAIM_TRACKING,
];

const POLL_MS = 3000;

/*
  While the only thing outstanding is learning whether recent texts arrived (story 15724): slower,
  since a report takes a minute or more, and silent - nothing is listed until one comes back bad.
*/
const DELIVERY_POLL_MS = 30000;

export enum PendingEmailStatus {
  Requested = 'Requested',
  Failed = 'Failed',
  /** A text that was sent but reached none of its numbers (story 15724). */
  Undeliverable = 'Undeliverable',
  /** Listed only for a group text that reached some of its numbers; errorMessage names the rest. */
  Delivered = 'Delivered',
}

/** A text that went out but did not reach everyone it was sent to (story 15724). */
export const isNotDelivered = (row: IPendingEmail) =>
  row.status === PendingEmailStatus.Undeliverable || row.status === PendingEmailStatus.Delivered;

/** Age past which a still-sending message is called out as taking longer than it should. */
export const STALE_AFTER_SECONDS = 60;

export enum PendingMessageChannel {
  Email = 'Email',
  Text = 'Text',
}

export interface IPendingEmail {
  id: number;
  subject: string;
  dateTime: string;
  /** Server-computed; see MsgHistoryPendingResponseBo.AgeSeconds for why it isn't derived here. */
  ageSeconds: number;
  status: PendingEmailStatus;
  /** Queued behind another message of the sender's - see MsgHistoryPendingResponseBo.Waiting. */
  waiting: boolean;
  /** Seconds since its send began, server-computed like ageSeconds; null until it has. */
  sendingSeconds: number | null;
  /*
    Why it failed - or, while it is still Requested, why it is being tried again: a sentence written
    for the sender (story 15723).
  */
  errorMessage: string | null;
  /** Requested and waiting to be tried again after a failure that passes: seconds until it is. */
  retryInSeconds: number | null;
  to: string[];
  channel: PendingMessageChannel;
  /** The Batch Send it belongs to; null for a single send. */
  batchId: number | null;
}

/*
  A Batch Send as one line (story 15723), counted over all its messages - "212 of 300 sent, 3 failed,
  85 waiting" - with its failed messages to open. See MsgHistoryPendingBatchResponseBo.
*/
export interface IPendingBatch {
  batchId: number;
  subject: string;
  dateTime: string;
  ageSeconds: number;
  /** 'Email', 'Text', or 'Mixed' when it sent both. */
  channel: string;
  total: number;
  /** Sent, delivered, or delivered to some of their numbers. */
  sentCount: number;
  /** Texts that reached none of their numbers (story 15724). */
  undeliverableCount: number;
  failedCount: number;
  /** Still to go: being sent, waiting its turn, or waiting to be tried again. */
  waitingCount: number;
  /** Of waitingCount, those waiting to be tried again. */
  retryingCount: number;
  heldCount: number;
  canceledCount: number;
  /** How long its message going out now has been sending; null when none is. */
  sendingSeconds: number | null;
  /** Its failed and undelivered messages that have not been dismissed. */
  failed: IPendingEmail[];
}

/*
  The sender's messages on one channel the send queue set aside, because one of them failed in a way
  every one of them would - RingCentral not connected, a mailbox that cannot send. Said once, however
  many there are. Resume and Cancel act on the channel.
*/
export interface IPendingHold {
  channel: PendingMessageChannel;
  /** Why they are on hold and what to do about it, written by the server. */
  reason: string;
  count: number;
}

interface IPendingResponse {
  messages: IPendingEmail[];
  batches: IPendingBatch[];
  holds: IPendingHold[];
  /** Recent texts whose delivery is still being learned - see MsgHistoryPendingListResponseBo. */
  textsAwaitingDelivery?: number;
}

const EMPTY: IPendingResponse = { messages: [], batches: [], holds: [] };

/** How many lines the indicator has: nothing to show is what stops the poll. */
export const pendingLineCount = (pending: IPendingResponse | undefined) =>
  (pending?.messages?.length ?? 0) + (pending?.batches?.length ?? 0) + (pending?.holds?.length ?? 0);

/** What a channel's messages are called, as many of them as there are. */
export const channelNoun = (channel: string, count: number) => {
  const plural = count !== 1;

  if (channel === PendingMessageChannel.Text) return plural ? 'texts' : 'text';
  if (channel === PendingMessageChannel.Email) return plural ? 'emails' : 'email';

  return plural ? 'messages' : 'message';
};

/*
  What the indicator calls the things it is sending. A mix is named as both rather than picking a
  winner, so nothing in flight goes unmentioned.
*/
const sendingNounFor = (channels: string[], count: number) => {
  const hasEmail = channels.some((channel) => channel !== PendingMessageChannel.Text);
  const hasText = channels.some((channel) => channel !== PendingMessageChannel.Email);

  if (hasEmail && hasText) return 'emails and texts';

  return channelNoun(hasText ? PendingMessageChannel.Text : PendingMessageChannel.Email, count);
};

export function usePendingEmails() {
  const { isAuthenticated } = useCasAuthenticated();
  const { userHasPermission } = useUserPermissions();
  const canSend = PENDING_MESSAGE_SENDERS.some(userHasPermission);
  const [hasPending, setHasPending] = useState(false);
  const [awaitingDelivery, setAwaitingDelivery] = useState(false);

  const query = useRetrieve({
    queryKey: [PENDING_EMAILS_QUERY_KEY],
    url: PENDING_EMAILS_QUERY_KEY,
    // The indicator is mounted app-wide but the endpoint is scoped to the senders above, so without
    // this gate every other user would 403 on each poll and onErrorDefaultHandler would turn it
    // into snackbar spam.
    enabled: isAuthenticated && canSend,
    // Catches an email sent from another tab, and returning to the app. Needed because once the
    // interval below goes false nothing else restarts it (the same-tab case is covered by the
    // send dialog invalidating this query).
    refetchOnWindowFocus: true,
    // Poll only while something is actually pending. With nothing pending this is false and the
    // query goes idle, so a user who isn't mid-send makes one request per page load and then
    // stops - this must never become a permanent per-user poller.
    refetchInterval: (() => {
      if (hasPending) return POLL_MS;
      if (awaitingDelivery) return DELIVERY_POLL_MS;
      return false;
    })(),
    onSuccess: (response: any) => {
      setHasPending(pendingLineCount(response?.result) > 0);
      setAwaitingDelivery((response?.result?.textsAwaitingDelivery ?? 0) > 0);
      // useAPI runs onSuccess inside queryFn and caches whatever it returns, so this must return
      // the response rather than only firing the side effect above.
      return response;
    },
  });

  const pending: IPendingResponse = query.data?.result ?? EMPTY;
  const { messages, batches, holds } = pending;

  const sendingMessages = messages.filter((row) => row.status === PendingEmailStatus.Requested);
  const sendingBatches = batches.filter((batch) => batch.waitingCount > 0);

  // Messages, not lines: a batch's line counts every message of it still to go.
  const sendingCount =
    sendingMessages.length + sendingBatches.reduce((sum, batch) => sum + batch.waitingCount, 0);

  // Counted separately from the lines: the error label reports how many failed, not how many are
  // listed, so one failure alongside seven in-flight reads "Error sending", not "(8)".
  const errorCount =
    messages.filter((row) => row.status === PendingEmailStatus.Failed).length +
    batches.reduce(
      (sum, batch) => sum + batch.failed.filter((row) => row.status === PendingEmailStatus.Failed).length,
      0
    );

  // Texts that went out but did not reach everyone - told apart from a failure to send.
  const notDeliveredCount =
    messages.filter(isNotDelivered).length +
    batches.reduce((sum, batch) => sum + batch.failed.filter(isNotDelivered).length, 0);

  const heldCount = holds.reduce((sum, hold) => sum + hold.count, 0);

  return {
    pending,
    count: pendingLineCount(pending),
    errorCount,
    sendingCount,
    sendingNoun: sendingNounFor(
      [...sendingMessages.map((row) => row.channel), ...sendingBatches.map((batch) => batch.channel)],
      sendingCount
    ),
    hasError: errorCount > 0,
    notDeliveredCount,
    heldCount,
    heldNoun: sendingNounFor(
      holds.map((hold) => hold.channel),
      heldCount
    ),
  };
}
